import type { ElasoRoleRef } from "@elaso/shared/types/authorization";
import { getActiveScope } from "./socket-owner";
import { coreStore } from "./stores/core-store";
import { uiStore } from "./stores/ui-store";

export type NotificationSoundOption = "bloop" | "plink";
export type NotificationSoundMode = "all" | "mentions-only" | "off";

const SOUND_PATHS: Record<NotificationSoundOption, string> = {
	bloop: `${import.meta.env.BASE_URL}notifications/bloop.mp3`,
	plink: `${import.meta.env.BASE_URL}notifications/plink.mp3`,
};

const audioCache = new Map<string, HTMLAudioElement>();

function getAudio(sound: NotificationSoundOption): HTMLAudioElement {
	const path = SOUND_PATHS[sound];
	let audio = audioCache.get(path);
	if (!audio) {
		audio = new Audio(path);
		audio.preload = "auto";
		audioCache.set(path, audio);
	}
	return audio;
}

export function preloadNotificationSounds(): void {
	for (const sound of Object.keys(SOUND_PATHS) as NotificationSoundOption[]) {
		getAudio(sound);
	}
}

let lastPlayedSound = 0;
const MIN_SOUND_INTERVAL = 750;

export function playNotificationSound(
	soundOverride?: NotificationSoundOption,
	isMention?: boolean
): void {
	const state = uiStore.getState();
	if (!soundOverride) {
		const mode = state.notificationSoundMode;
		if (mode === "off") return;
		if (mode === "mentions-only" && !isMention) return;
	}
	const sound =
		soundOverride ??
		(isMention ? state.mentionNotificationSound : state.notificationSound);
	const now = Date.now();
	if (now - lastPlayedSound < MIN_SOUND_INTERVAL) return;

	const audio = getAudio(sound);
	audio.currentTime = 0;
	audio.play().catch((err) => {
		console.warn("[NotificationSound] Failed to play sound:", err);
	});

	lastPlayedSound = now;
}

function isMuteActive(muteStatus: string | null | undefined): boolean {
	if (muteStatus === undefined) return false;
	if (muteStatus === null) return true;
	return new Date(muteStatus) > new Date();
}

function isChannelOrCategoryMuted(channelId: string): boolean {
	const { channels, categoryMuteStatuses } = coreStore.getState();
	const channel = channels.get(channelId);
	if (!channel) return false;
	if (isMuteActive(channel.muteStatus)) return true;
	if (channel.categoryId) {
		return isMuteActive(categoryMuteStatuses.get(channel.categoryId));
	}
	return false;
}

export function shouldPlayNotification(
	messageChannelId: string,
	messageAuthorId: string,
	currentUserId: string | undefined
): boolean {
	// Notification sounds are an /app concept. On /operations and /organization
	// the checks below read state that is not mounted (the /app grid, the /app
	// session's focus rules), so they would answer "notify" for nearly every
	// message (TECH-3263). This runs before the hidden-tab check on purpose, so
	// a backgrounded ops tab stays silent too.
	if (getActiveScope() !== "app") return false;
	if (!currentUserId) return false;
	if (messageAuthorId === currentUserId) return false;
	if (isChannelOrCategoryMuted(messageChannelId)) return false;

	const isAppHidden = document.hidden || !document.hasFocus();
	if (isAppHidden) return true;

	const gridItems = uiStore.getState().gridItems;
	const firstItem = gridItems[0];
	if (!firstItem) return true;

	const activeTab = firstItem.tabs[firstItem.activeTabIndex];
	if (activeTab?.type !== "textChannel") return true;

	const activeChannelId = (activeTab.params as { channelId?: string })
		?.channelId;
	return activeChannelId !== messageChannelId;
}

export function messageContainsMention(
	richContent: unknown,
	currentUserId: string,
	userRoles: ElasoRoleRef[]
): boolean {
	function traverse(node: unknown): boolean {
		if (!node || typeof node !== "object") return false;
		const n = node as Record<string, unknown>;
		if (n.type === "mention" && n.attrs) {
			const attrs = n.attrs as Record<string, unknown>;
			if (attrs.id && typeof attrs.id === "string") {
				if (attrs.roleType === "workspace") {
					// attrs.id is the role's Mongo ObjectId (named-kind ref).
					// Pre-migration mentions stored attrs.id as the role name, so they
					// no longer match here. That is intentional, there is no backfill.
					if (
						userRoles.some(
							(r) =>
								r.kind === "named" &&
								r.id === attrs.id &&
								r.workspaceId === attrs.workspaceId
						)
					)
						return true;
				} else if (attrs.id === currentUserId) {
					return true;
				}
			}
		}
		if (Array.isArray(n.content)) {
			for (const child of n.content) {
				if (traverse(child)) return true;
			}
		}
		return false;
	}

	return traverse(richContent);
}
